// CricketDashboard — the admin's CONTROL ROOM for the flagship game. One screen,
// mobile-first, answering the owner's questions at a glance:
//   • how much is the HOUSE up/down on cricket (total, this week, by pillar)
//   • every agent: their assigned %s, what they earned (by pillar), how many
//     players they hold, their players' turnover, and what the HOUSE nets from
//     that agent's players — direct agents by default, full tree on toggle
//   • top winning / top losing players (with their agent)
//   • volume: turnover, settled/open bets, live exposure
//   • the recent admin money trail
// Backed by GET /api/admin/cricket/dashboard (cricket's own isolated books).

import { useCallback, useEffect, useState } from 'react';
import { levelName } from '../../../lib/levelNames';
import { getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';
import Icon from '@baaligames/shared-ui/components/Icon';

type Dash = {
  scope: string;
  house: { balance: number; netTotal: number; netWeek: number; byKind: { kind: string; net: number; week: number }[] };
  volume: { turnover: number; turnoverWeek: number; settledBets: number; openBets: number; openAtRisk: number; matchesWithSettledBets: number };
  agents: {
    id: number; username: string; level: number; parentUsername: string | null; direct: boolean;
    rates: { profitSharePct: number; commissionSharePct: number };
    cricketBalance: number; earnedTotal: number; earnedWeek: number;
    byPillar: { profitShare: number; commissionShare: number };
    players: number; playersWeekTurnover: number; houseNetFromPlayers: number;
  }[];
  topWinners: { id: number; username: string; agent: string | null; net: number; staked: number }[];
  topLosers: { id: number; username: string; agent: string | null; net: number; staked: number }[];
  recent: { id: number; kind: string; amount: number; matchId: string | null; counterparty: string | null; note: string | null; at: string }[];
};

const inr = (n: number) => `₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;
const signed = (n: number) => (n < 0 ? `−${inr(n)}` : `+${inr(n)}`);
const KIND_LABEL: Record<string, string> = {
  house_win_loss: 'House win/loss (P&L residual)',
  commission: 'Exchange commission',
  agent_carveout_residual: 'Carve-out residual',
  refund_funded: 'Refunds funded',
  correction: 'Corrections',
  manual_credit: 'Manual credits',
  manual_debit: 'Manual debits',
};

function Money({ n, big }: { n: number; big?: boolean }) {
  return <span className={`font-mono font-black ${big ? 'text-xl' : ''} ${n < 0 ? 'text-red-600 dark:text-red-400' : n > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'}`}>{signed(n)}</span>;
}

function Card({ label, children, sub, icon }: { label: string; children: any; sub?: string; icon?: string }) {
  return (
    <div className="flex-1 min-w-[150px] bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">{icon && <Icon name={icon} size={12} />}{label}</div>
      <div>{children}</div>
      {sub && <div className="text-[11px] text-gray-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function CricketDashboard() {
  const [data, setData] = useState<Dash | null>(null);
  const [scope, setScope] = useState<'direct' | 'all'>('direct');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const t = getAdminAccessToken();
      const res = await fetch(`/api/admin/cricket/dashboard?scope=${scope}`, { headers: t ? { authorization: `Bearer ${t}` } : {} });
      const j = await res.json();
      if (!res.ok || !j.success) throw new Error(j?.error?.message || 'Failed to load');
      setData(j); setErr('');
    } catch (e: any) { setErr(e.message); }
  }, [scope]);
  useEffect(() => { void load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  if (err) return <div className="p-6 text-center text-red-500 font-bold">{err}</div>;
  if (!data) return <div className="p-6 text-center text-gray-400 animate-pulse">Loading cricket dashboard…</div>;

  const h = data.house, v = data.volume;

  return (
    <div className="space-y-5 pb-10">
      {/* ── House headline ── */}
      <div className="flex flex-wrap gap-3">
        <Card label="House net (all time)" icon="account_balance" sub="Cricket book — positive = house is up">
          <Money n={h.netTotal} big />
        </Card>
        <Card label="This week" icon="calendar_today" sub="Last 7 days across all pillars">
          <Money n={h.netWeek} big />
        </Card>
        <Card label="Open exposure" icon="warning" sub={`${v.openBets} open bet(s) — player money at risk with the house`}>
          <span className="font-mono font-black text-xl text-amber-600">{inr(v.openAtRisk)}</span>
        </Card>
        <Card label="Turnover" icon="sync_alt" sub={`${v.settledBets} settled bets · ${v.matchesWithSettledBets} matches · week ${inr(v.turnoverWeek)}`}>
          <span className="font-mono font-black text-xl text-gray-900 dark:text-white">{inr(v.turnover)}</span>
        </Card>
      </div>

      {/* ── Where the house money comes from / goes ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-3">House breakdown — where it comes from, where it goes</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {h.byKind.map((k) => (
            <div key={k.kind} className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/50 px-3 py-2">
              <div className="text-[11px] font-bold text-gray-500 truncate">{KIND_LABEL[k.kind] || k.kind}</div>
              <div className="flex items-baseline justify-between gap-2">
                <Money n={k.net} />
                <span className="text-[10px] text-gray-400">wk {signed(k.week)}</span>
              </div>
            </div>
          ))}
          {h.byKind.length === 0 && <div className="text-sm text-gray-400 py-2">No settled money yet — entries appear the moment bets settle.</div>}
        </div>
      </div>

      {/* ── Agents ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Agents — rates, earnings & what their players bring the house</div>
          <div className="bg-gray-100 dark:bg-gray-700 p-0.5 rounded-lg flex text-[11px] font-bold">
            <button onClick={() => setScope('direct')} className={`px-3 py-1 rounded-md transition ${scope === 'direct' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'text-gray-500'}`}>Direct only</button>
            <button onClick={() => setScope('all')} className={`px-3 py-1 rounded-md transition ${scope === 'all' ? 'bg-white dark:bg-gray-600 shadow-sm' : 'text-gray-500'}`}>Whole tree</button>
          </div>
        </div>
        {/* mobile: cards; desktop: table */}
        <div className="space-y-2 md:hidden">
          {data.agents.map((a) => (
            <div key={a.id} className="rounded-xl border border-gray-100 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-900/40 p-3">
              <div className="flex items-center justify-between">
                <span className="font-black text-sm text-gray-900 dark:text-white">{a.username} <span className="text-gray-400 font-medium">#{a.id}</span></span>
                <Money n={a.earnedTotal} />
              </div>
              {!a.direct && a.parentUsername && <div className="text-[10px] text-gray-400">under {a.parentUsername} · {levelName(a.level)}</div>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-[10px] font-bold bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 rounded px-1.5 py-0.5">P&L {a.rates.profitSharePct}%</span>
                <span className="text-[10px] font-bold bg-purple-100 dark:bg-purple-500/15 text-purple-700 dark:text-purple-400 rounded px-1.5 py-0.5">Comm {a.rates.commissionSharePct}%</span>
                <span className="text-[10px] font-bold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded px-1.5 py-0.5">{a.players} player{a.players === 1 ? '' : 's'}</span>
              </div>
              <div className="grid grid-cols-2 gap-1.5 mt-2 text-center">
                <div className="rounded bg-white dark:bg-gray-800 py-1"><div className="text-[9px] text-gray-400 uppercase">P&L</div><Money n={a.byPillar.profitShare} /></div>
                <div className="rounded bg-white dark:bg-gray-800 py-1"><div className="text-[9px] text-gray-400 uppercase">House nets</div><Money n={a.houseNetFromPlayers} /></div>
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-[10px] uppercase tracking-widest text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-2 pr-3">Agent</th><th className="text-right px-2">P&L %</th><th className="text-right px-2">Comm %</th>
              <th className="text-right px-2">Players</th><th className="text-right px-2">Wk turnover</th>
              <th className="text-right px-2">Earned (P&L)</th><th className="text-right px-2">Earned total</th>
              <th className="text-right px-2">Balance</th><th className="text-right pl-2">House nets from their players</th>
            </tr></thead>
            <tbody>
              {data.agents.map((a) => (
                <tr key={a.id} className="border-b border-gray-100 dark:border-gray-700/40 last:border-0">
                  <td className="py-2 pr-3">
                    <span className="font-bold text-gray-900 dark:text-white">{a.username}</span> <span className="text-gray-400">#{a.id}</span>
                    {!a.direct && a.parentUsername && <div className="text-[10px] text-gray-400">under {a.parentUsername} · {levelName(a.level)}</div>}
                  </td>
                  <td className="text-right px-2 font-mono">{a.rates.profitSharePct}%</td>
                  <td className="text-right px-2 font-mono">{a.rates.commissionSharePct}%</td>
                  <td className="text-right px-2">{a.players}</td>
                  <td className="text-right px-2 font-mono">{inr(a.playersWeekTurnover)}</td>
                  <td className="text-right px-2"><Money n={a.byPillar.profitShare} /></td>
                  <td className="text-right px-2"><Money n={a.earnedTotal} /></td>
                  <td className="text-right px-2"><Money n={a.cricketBalance} /></td>
                  <td className="text-right pl-2"><Money n={a.houseNetFromPlayers} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {data.agents.length === 0 && <div className="text-sm text-gray-400 py-3 text-center">No agents in this scope yet.</div>}
        <p className="text-[10px] text-gray-400 mt-2">"House nets from their players" = the admin share booked on bets placed by that agent's direct players. Positive = those players are profitable for the house.</p>
      </div>

      {/* ── Top players ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {([['Top winning players (house pays)', data.topWinners, 'trending_up'], ['Top losing players (house earns)', data.topLosers, 'trending_down']] as const).map(([title, rows, icon]) => (
          <div key={title} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2"><Icon name={icon} size={13} />{title}</div>
            {rows.length === 0 ? <div className="text-sm text-gray-400 py-2">Nothing settled yet.</div> : (
              <div className="space-y-1.5">
                {rows.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-gray-50 dark:bg-gray-900/40 px-3 py-1.5">
                    <div className="min-w-0">
                      <span className="font-bold text-sm text-gray-900 dark:text-white">{p.username}</span>
                      <span className="text-[10px] text-gray-400 ml-1.5">#{p.id}{p.agent ? ` · agent ${p.agent}` : ''}</span>
                    </div>
                    <div className="text-right shrink-0">
                      <Money n={p.net} />
                      <div className="text-[9px] text-gray-400">staked {inr(p.staked)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* ── Recent money trail ── */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2">Recent house entries (hash-chained ledger)</div>
        {data.recent.length === 0 ? <div className="text-sm text-gray-400 py-2">Ledger is empty — entries appear as bets settle.</div> : (
          <div className="space-y-1">
            {data.recent.map((x) => (
              <div key={x.id} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-gray-100 dark:border-gray-700/40 last:border-0">
                <span className="font-bold text-gray-600 dark:text-gray-300 truncate">{KIND_LABEL[x.kind] || x.kind}{x.matchId ? ` · match ${x.matchId}` : ''}{x.counterparty ? ` · ${x.counterparty}` : ''}</span>
                <span className="text-gray-400 shrink-0">{new Date(x.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                <Money n={x.amount} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
